import { useContext } from 'react';
import { useQuery } from 'react-query';
import { ServerErrorResponse } from 'data_sources/constants';
import { RiskPoolOrganization } from '../../_types/risk_pool';
import { generateRiskPoolMetrics } from './riskPoolMetricsGenerator';
import { getRiskPoolOrganizations } from './useRiskPoolOrganizations';
import { useRiskPoolDemoData } from './useRiskPoolDemoData';
import { AppContext } from 'views/App/context';
import { RiskPoolOrgContext } from '../RiskPoolOrganizations/Detail/RiskPoolOrgContext';
import _ from 'lodash';

interface RiskPoolOrganizationMetricsParams {
  date?: string;
  search?: string;
  compliance?: string;
  [key: string]: any;
}

export default function useRiskPoolOrganizationMetrics(
  childOrgId: string,
  params: RiskPoolOrganizationMetricsParams = {},
  options: object = {},
  onError?: (errors: ServerErrorResponse) => void
) {
  const { currentOrganizationId } = useContext(AppContext);
  const parentOrgId = currentOrganizationId || childOrgId;
  const isDemoDataEnabled = useRiskPoolDemoData();

  // Use the point-in-time date from context (driven by the dashboard DatePicker).
  // getRiskPoolOrganizations will fall back to yesterday if date is still undefined.
  const riskPoolOrgContext = useContext(RiskPoolOrgContext);
  const date = params.date || riskPoolOrgContext?.date;

  const finalParams = {
    ...params,
    ...(date ? { date } : {}),
  };

  return useQuery<RiskPoolOrganization, Error>(
    ['risk_pool_organization_metrics', parentOrgId, childOrgId, finalParams],
    async ({ signal }): Promise<RiskPoolOrganization> => {
      try {
        if (isDemoDataEnabled) {
          // Get the filtered data using the same logic as useRiskPoolMetrics
          const data = generateRiskPoolMetrics(childOrgId, finalParams);

          // Find the specific organization by ID
          const organization = _.find(data, (org) => org.id === childOrgId);

          if (!organization) {
            throw new Error(`Organization with ID ${childOrgId} not found`);
          }

          return organization;
        }

        // Fetch from API: get all organizations and find the matching one
        const response = await getRiskPoolOrganizations(parentOrgId, finalParams, signal, onError);
        const organization = _.find(response.organizations, (org) => org.id === childOrgId);

        if (!organization) {
          throw new Error(`Organization with ID ${childOrgId} not found`);
        }

        return organization;
      } catch (err) {
        if (onError) onError(err as ServerErrorResponse);
        throw err;
      }
    },
    {
      ...options,
      enabled: !!childOrgId && !!parentOrgId,
    }
  );
}
